import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useOrderByParams } from '~/hooks/order-by';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import type { NetworkDeviceGroups__DeviceGroup as DeviceGroup } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      description={t('pages.device_groups.list.empty_state.description')}
      header={t('pages.device_groups.list.empty_state.header')}
    />
  );
};

export interface TableProps {
  deviceGroups: readonly DeviceGroup[];
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const Table = ({ deviceGroups, loading, onCheck, selectedIds }: TableProps) => {
  const columns = useColumns();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      defaultShow: !isSmallAndDown,
      createdAt: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  return (
    <ScrollableDataGrid
      checkboxSelection={!isSmallAndDown}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      isRowSelectable={({ row }) => row.canDestroy.value}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowSelectionModelChange={onCheck}
      onRowClick={({ row: group }: GridRowParams<DeviceGroup>) => navigate(String(group.id))}
      rows={deviceGroups}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
